// src/pages/Backups.js
import React, { useState, useEffect, useCallback } from 'react';
import { Database, Plus, Play, Trash2, RefreshCw, CheckCircle, AlertTriangle, Clock, X, Save, FileText, Search } from 'lucide-react';
import api from '../utils/api';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';

const STATUS_ICON = {
  idle: <Clock size={14} color="var(--text-secondary)" />,
  running: <span className="spinner" style={{ width: 14, height: 14 }} />,
  success: <CheckCircle size={14} color="var(--success)" />,
  failed: <AlertTriangle size={14} color="var(--danger)" />,
};

function JobModal({ job, onClose, onSaved }) {
  const isEdit = !!job?.id;
  const empty = { name: '', type: 'manual', source_type: 'files', source_path: '/var/www', destination_type: 'local', destination_config: { path: '/backups' }, schedule_cron: '0 2 * * *', retention_days: 30, notes: '' };
  const [f, setF] = useState(isEdit ? { ...job, destination_config: typeof job.destination_config === 'string' ? JSON.parse(job.destination_config || '{}') : (job.destination_config || {}) } : empty);
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const setDest = (k, v) => setF(p => ({ ...p, destination_config: { ...p.destination_config, [k]: v } }));

  const save = async () => {
    if (!f.name) return toast.error('Numele este obligatoriu');
    setLoading(true);
    try {
      if (isEdit) { await api.put(`/backups/${job.id}`, f); toast.success('Job actualizat'); }
      else { await api.post('/backups', f); toast.success('Job creat'); }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.error || 'Eroare'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-xl">
        <div className="modal-header"><span className="modal-title"><Database size={16} /> {isEdit ? 'Editare Job' : 'Job Backup Nou'}</span><button className="btn-icon" onClick={onClose}><X size={16} /></button></div>
        <div className="modal-body">
          <div className="form-grid form-grid-2">
            <div className="form-group"><label className="form-label">Nume *</label><input className="form-input" value={f.name} onChange={e => set('name', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Tip</label>
              <select className="form-select" value={f.type} onChange={e => set('type', e.target.value)}>
                <option value="manual">Manual</option><option value="scheduled">Programat</option>
              </select></div>
            <div className="form-group"><label className="form-label">Sursă</label>
              <select className="form-select" value={f.source_type} onChange={e => set('source_type', e.target.value)}>
                <option value="files">Fișiere</option><option value="database">Bază de date</option><option value="docker">Docker Container</option><option value="full">Complet</option>
              </select></div>
            <div className="form-group"><label className="form-label">Cale/Sursă</label><input className="form-input" value={f.source_path || ''} onChange={e => set('source_path', e.target.value)} placeholder="/var/www, container-id..." /></div>
            <div className="form-group"><label className="form-label">Destinație</label>
              <select className="form-select" value={f.destination_type} onChange={e => set('destination_type', e.target.value)}>
                <option value="local">Local</option><option value="sftp">SFTP</option><option value="s3">Amazon S3</option><option value="ftp">FTP</option>
              </select></div>
            <div className="form-group"><label className="form-label">Cale destinație</label><input className="form-input" value={f.destination_config?.path || ''} onChange={e => setDest('path', e.target.value)} placeholder="/backups, s3://bucket..." /></div>
            {f.type === 'scheduled' && (
              <div className="form-group"><label className="form-label">Cron Schedule</label><input className="form-input" value={f.schedule_cron || ''} onChange={e => set('schedule_cron', e.target.value)} placeholder="0 2 * * *" /></div>
            )}
            <div className="form-group"><label className="form-label">Retenție (zile)</label><input className="form-input" type="number" value={f.retention_days} onChange={e => set('retention_days', e.target.value)} /></div>
          </div>
          <div className="form-group"><label className="form-label">Notițe</label><textarea className="form-textarea" value={f.notes || ''} onChange={e => set('notes', e.target.value)} /></div>
        </div>
        <div className="modal-footer"><button className="btn btn-secondary" onClick={onClose}>Anulare</button><button className="btn btn-primary" onClick={save} disabled={loading}>{loading ? <span className="spinner" /> : <><Save size={14} /> {isEdit ? 'Actualizare' : 'Creare'}</>}</button></div>
      </div>
    </div>
  );
}

function LogsModal({ jobId, jobName, onClose }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    api.get(`/backups/${jobId}/logs`)
      .then(r => setLogs(r.data))
      .catch(() => toast.error('Eroare la logs'))
      .finally(() => setLoading(false));
  }, [jobId]);

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-xl">
        <div className="modal-header"><span className="modal-title"><FileText size={16} /> Logs — {jobName}</span><button className="btn-icon" onClick={onClose}><X size={16} /></button></div>
        <div className="modal-body">
          {loading ? <div className="loading-center"><span className="spinner" /></div> : logs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-secondary)' }}>Niciun log disponibil</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {logs.map(l => (
                <div key={l.id} style={{ background: 'var(--bg-hover)', borderRadius: 8, padding: '10px 14px', border: `1px solid ${l.status === 'failed' ? 'var(--danger)' : l.status === 'success' ? 'var(--success)' : 'var(--border)'}33` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>{STATUS_ICON[l.status]}<span style={{ fontWeight: 600, fontSize: 13 }}>{l.status}</span>{l.duration && <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{l.duration}s</span>}</div>
                    <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{new Date(l.created_at).toLocaleString('ro-RO')}</span>
                  </div>
                  {l.message && <div style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>{l.message}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="modal-footer"><button className="btn btn-secondary" onClick={onClose}>Închidere</button></div>
      </div>
    </div>
  );
}

export default function Backups() {
  const [list, setList] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [logsJob, setLogsJob] = useState(null);
  const [running, setRunning] = useState({});
  const toast = useToast();
  const { hasRole } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [jobs, st] = await Promise.all([api.get('/backups'), api.get('/backups/stats')]);
      setList(jobs.data); setStats(st.data);
    } catch (e) { toast.error('Eroare la încărcare'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runJob = async (id, name) => {
    setRunning(p => ({ ...p, [id]: true }));
    try {
      await api.post(`/backups/${id}/run`);
      toast.success(`Backup "${name}" pornit`);
      setTimeout(() => { setRunning(p => ({ ...p, [id]: false })); load(); }, 3000);
    } catch (e) { toast.error(e.response?.data?.error || 'Eroare'); setRunning(p => ({ ...p, [id]: false })); }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Ștergi job-ul "${name}"?`)) return;
    try { await api.delete(`/backups/${id}`); toast.success('Job șters'); load(); }
    catch (e) { toast.error('Eroare'); }
  };

  return (
    <div className="fade-in">
      <div className="page-header">
        <div><div className="page-title">Backup Manager</div><div className="page-subtitle">Configurare și monitorizare backup-uri automate</div></div>
        <button className="btn btn-primary" onClick={() => setModal({ new: true })}><Plus size={14} /> Job Nou</button>
      </div>

      {stats && (
        <div className="stats-grid" style={{ marginBottom: 20 }}>
          {[
            { label: 'Total Job-uri', value: stats.total, color: 'var(--accent)', icon: Database },
            { label: 'Active', value: stats.active, color: 'var(--success)', icon: CheckCircle },
            { label: 'Eșuate', value: stats.failed, color: 'var(--danger)', icon: AlertTriangle },
            { label: 'Ultima rulare', value: stats.lastRun ? new Date(stats.lastRun).toLocaleDateString('ro-RO') : '—', color: 'var(--text-secondary)', icon: Clock },
          ].map(k => (
            <div key={k.label} className="stat-card">
              <div className="stat-icon" style={{ background: `${k.color}20`, marginBottom: 10 }}><k.icon size={16} color={k.color} /></div>
              <div style={{ fontSize: 20, fontWeight: 800, color: k.color }}>{k.value}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 3 }}>{k.label}</div>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
          <button className="btn btn-secondary" onClick={load}><RefreshCw size={14} /></button>
        </div>
        {loading ? <div className="loading-center"><span className="spinner" /></div> : (
          <div className="table-wrapper">
            <table>
              <thead><tr><th>NUME</th><th>TIP SURSĂ</th><th>DESTINAȚIE</th><th>SCHEDULE</th><th>STATUS</th><th>ULTIMA RULARE</th><th>DURATĂ</th><th>ACȚIUNI</th></tr></thead>
              <tbody>
                {list.length === 0 && <tr><td colSpan={8} style={{ textAlign: 'center', padding: 30, color: 'var(--text-secondary)' }}>Niciun job de backup</td></tr>}
                {list.map(j => (
                  <tr key={j.id}>
                    <td><div style={{ fontWeight: 600 }}>{j.name}</div>{j.notes && <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{j.notes}</div>}</td>
                    <td><span className="badge badge-neutral">{j.source_type}</span></td>
                    <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{j.destination_type}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{j.schedule_cron || '—'}</td>
                    <td><div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>{STATUS_ICON[j.status] || STATUS_ICON.idle}<span style={{ fontSize: 12 }}>{j.status}</span></div></td>
                    <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{j.last_run ? new Date(j.last_run).toLocaleString('ro-RO') : '—'}</td>
                    <td style={{ fontSize: 12 }}>{j.last_duration ? `${j.last_duration}s` : '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn-icon" title="Rulare" style={{ color: 'var(--success)' }} onClick={() => runJob(j.id, j.name)} disabled={running[j.id]}>{running[j.id] ? <span className="spinner" style={{ width: 14, height: 14 }} /> : <Play size={13} />}</button>
                        <button className="btn-icon" title="Logs" onClick={() => setLogsJob(j)}><FileText size={13} /></button>
                        <button className="btn-icon" title="Editare" onClick={() => setModal(j)}><Save size={13} /></button>
                        {hasRole('superadmin', 'admin') && <button className="btn-icon" style={{ color: 'var(--danger)' }} onClick={() => del(j.id, j.name)}><Trash2 size={13} /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && <JobModal job={modal.new ? null : modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {logsJob && <LogsModal jobId={logsJob.id} jobName={logsJob.name} onClose={() => setLogsJob(null)} />}
    </div>
  );
}
